import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { SalesLayout } from '../components/crm/SalesLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Building2, Plus, Search } from 'lucide-react';
import { crmFetch } from '../components/crm/crmApi';
import { AccountModal } from '../components/crm/AccountModal';
import { HEALTH_META, statusMeta, fmtDate, daysAgoLabel } from '../components/crm/constants';

const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'active_client', label: 'Active Clients' },
  { key: 'prospect', label: 'Prospects' },
  { key: 'inactive', label: 'Inactive' },
];

export default function Accounts() {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const isAdmin = userProfile?.role === 'jobasti_admin';
  const [accounts, setAccounts] = useState([]);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableMissing, setTableMissing] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    const res = await crmFetch('/accounts');
    if (res.tableMissing) setTableMissing(true);
    else if (res.success) { setAccounts(res.data || []); setTableMissing(false); }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    crmFetch('/sales-users').then((r) => setOwners(r.data || []));
  }, [load]);

  const q = search.trim().toLowerCase();
  const filtered = accounts.filter((a) => {
    if (statusFilter !== 'all' && a.status !== statusFilter) return false;
    if (!q) return true;
    return [a.name, a.industry, a.location].some((v) => (v || '').toLowerCase().includes(q));
  });

  return (
    <SalesLayout>
      <div data-testid="accounts-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Accounts</h1>
            <p className="text-sm text-gray-500 mt-1">Your recruitment client companies.</p>
          </div>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setModalOpen(true)} data-testid="new-account-btn">
            <Plus className="w-4 h-4 mr-2" /> New Account
          </Button>
        </div>

        {tableMissing && (
          <Card className="border-amber-300 bg-amber-50" data-testid="crm-migration-notice">
            <CardContent className="p-6 text-sm text-amber-800">
              CRM tables not migrated yet — run <code className="px-1 bg-amber-100 rounded">2026-08-30-crm-phase1.sql</code> in the Supabase SQL Editor.
            </CardContent>
          </Card>
        )}

        {!tableMissing && (
          <>
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input className="pl-9" placeholder="Search accounts…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="accounts-search-input" />
              </div>
              <div className="flex gap-1">
                {STATUS_FILTERS.map((f) => (
                  <button
                    key={f.key}
                    onClick={() => setStatusFilter(f.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      statusFilter === f.key ? 'bg-blue-600 text-white' : 'bg-white border text-gray-600 hover:bg-gray-100'
                    }`}
                    data-testid={`accounts-filter-${f.key}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50/60 text-left text-xs text-gray-500 uppercase tracking-wide">
                        <th className="px-4 py-3 font-medium">Account</th>
                        <th className="px-4 py-3 font-medium">Status</th>
                        <th className="px-4 py-3 font-medium">Health</th>
                        <th className="px-4 py-3 font-medium">Last Contact</th>
                        <th className="px-4 py-3 font-medium">Next Contact</th>
                        <th className="px-4 py-3 font-medium text-center">Contacts</th>
                        <th className="px-4 py-3 font-medium text-center">Open Tasks</th>
                        {isAdmin && <th className="px-4 py-3 font-medium">Owner</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {loading && (
                        <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-400">Loading…</td></tr>
                      )}
                      {!loading && filtered.length === 0 && (
                        <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-400">
                          {accounts.length === 0 ? 'No accounts yet — create your first one.' : 'No accounts match your filters.'}
                        </td></tr>
                      )}
                      {filtered.map((a) => {
                        const sm = statusMeta(a.status);
                        const hm = HEALTH_META[a.health];
                        return (
                          <tr
                            key={a.id}
                            onClick={() => navigate(`/accounts/${a.id}`)}
                            className="border-b last:border-0 hover:bg-blue-50/40 cursor-pointer transition-colors"
                            data-testid={`account-row-${a.id}`}
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center shrink-0">
                                  <Building2 className="w-4 h-4" />
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">{a.name}</p>
                                  <p className="text-xs text-gray-500">{[a.industry, a.location].filter(Boolean).join(' · ') || '—'}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3"><Badge className={`${sm.className} border-0`}>{sm.label}</Badge></td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${hm.text}`}>
                                <span className={`w-2.5 h-2.5 rounded-full ${hm.dot}`} />{hm.label}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-gray-600">{daysAgoLabel(a.daysSinceContact)}</td>
                            <td className="px-4 py-3 text-gray-600">{a.nextContactDate ? fmtDate(a.nextContactDate) : '—'}</td>
                            <td className="px-4 py-3 text-center text-gray-600">{a.contactCount}</td>
                            <td className="px-4 py-3 text-center">
                              <span className={a.overdueTaskCount > 0 ? 'text-rose-600 font-semibold' : 'text-gray-600'}>
                                {a.openTaskCount}{a.overdueTaskCount > 0 ? ` (${a.overdueTaskCount} overdue)` : ''}
                              </span>
                            </td>
                            {isAdmin && <td className="px-4 py-3 text-gray-600">{a.ownerName || '—'}</td>}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      <AccountModal open={modalOpen} onOpenChange={setModalOpen} onSaved={load} owners={owners} />
    </SalesLayout>
  );
}
